import React, { useCallback, useEffect, useState } from 'react';
import { get, post, put, del } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import './UsersCRUD.css';

// The endpoint lives on the `employees` API rather than one of its own: both
// concern people, and adding a path avoided hand-deriving a second API stack.
const API = { apiName: 'employees', path: '/users' };

const ADMIN_GROUP = 'admins';

// Reading the body is the only way to see the handler's error message; Amplify
// throws before you can get at it otherwise.
async function callApi(operation) {
    try {
        const { body } = await operation.response;
        return await body.json();
    } catch (err) {
        const detail = await err.response?.body?.text?.().catch(() => null);
        let message = err.message;
        try {
            if (detail) message = JSON.parse(detail).error || message;
        } catch { /* keep err.message */ }
        throw new Error(message);
    }
}

const UsersCRUD = () => {
    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState('');
    const [isAdmin, setIsAdmin] = useState(null);
    const [draft, setDraft] = useState({ username: '', email: '', name: '', groups: [], invite: true });

    // Gate the UI on the group claim in the ID token. This is convenience only
    // — usersHandler re-checks membership on every request, because a token
    // claim is trivially editable in a browser.
    useEffect(() => {
        fetchAuthSession()
            .then(session => {
                const claims = session.tokens?.idToken?.payload ?? {};
                setIsAdmin((claims['cognito:groups'] || []).includes(ADMIN_GROUP));
            })
            .catch(() => setIsAdmin(false));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await callApi(get(API));
            setUsers(data.users || []);
            setGroups(data.groups || []);
        } catch (err) {
            setError(err.message || 'Failed to load users');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

    const run = async (label, operation, successMessage) => {
        setBusy(label);
        setError('');
        setNotice('');
        try {
            await callApi(operation);
            setNotice(successMessage);
            await load();
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy('');
        }
    };

    const createUser = async event => {
        event.preventDefault();
        if (!draft.username.trim() || !draft.email.trim()) {
            setError('Username and email are both required');
            return;
        }
        // A user in no group can sign in but sees nothing; that state has only
        // ever been a mistake, so refuse it here.
        if (!draft.groups.length) {
            setError('Pick at least one group');
            return;
        }
        await run('create', post({ ...API, options: { body: { ...draft } } }),
            draft.invite
                ? `Invited ${draft.username} — an email with a temporary password and a sign-in link is on its way to ${draft.email}`
                : `Created ${draft.username} without sending an email`);
        setDraft({ username: '', email: '', name: '', groups: [], invite: true });
    };

    const toggleGroup = (user, group) => {
        const next = user.groups.includes(group)
            ? user.groups.filter(g => g !== group)
            : [...user.groups, group];
        return run(`groups:${user.username}`,
            put({ ...API, options: { body: { username: user.username, groups: next } } }),
            `Updated groups for ${user.username}`);
    };

    const setEnabled = (user, enabled) =>
        run(`enabled:${user.username}`,
            put({ ...API, options: { body: { username: user.username, enabled } } }),
            `${enabled ? 'Enabled' : 'Disabled'} ${user.username}`);

    const resendInvite = user =>
        run(`resend:${user.username}`,
            post({ ...API, options: { body: { action: 'resendInvite', username: user.username } } }),
            `Re-sent the invitation to ${user.username}`);

    const removeUser = user => {
        if (!window.confirm(`Delete ${user.username}? This cannot be undone.`)) return;
        // Unlike post/put, Amplify's del() silently drops a body, so the
        // username travels in the query string.
        return run(`delete:${user.username}`,
            del({ ...API, path: `${API.path}?username=${encodeURIComponent(user.username)}` }),
            `Deleted ${user.username}`);
    };

    const header = (
        <div className="page-header">
            <h2 className="page-title">Users</h2>
            <p className="page-subtitle">
                Sign-up is disabled, so accounts are created here. An invitation emails a
                temporary password; the person sets their own on first sign-in.
            </p>
        </div>
    );

    if (isAdmin === null) return <div className="users-page">{header}<p className="users-muted">Checking permissions…</p></div>;

    if (!isAdmin) {
        return (
            <div className="users-page">
                {header}
                <p className="users-error">
                    This page is limited to the {ADMIN_GROUP} group.
                </p>
            </div>
        );
    }

    return (
        <div className="users-page">
            {header}

            {error && <p className="users-error">{error}</p>}
            {notice && <p className="users-notice">{notice}</p>}

            <form className="users-new" onSubmit={createUser}>
                <h3 className="users-section">Add someone</h3>
                <div className="users-fields">
                    {/* The caption is one element per field, so all three inputs sit on
                        the same line. "Full name" plus a bare "optional" span used to make
                        that label two rows tall, pushing its input out of alignment. */}
                    <label>
                        <span className="users-caption">Username</span>
                        <input value={draft.username} autoComplete="off"
                            onChange={e => setDraft({ ...draft, username: e.target.value })} />
                    </label>
                    <label>
                        <span className="users-caption">Email</span>
                        <input type="email" value={draft.email} autoComplete="off"
                            onChange={e => setDraft({ ...draft, email: e.target.value })} />
                    </label>
                    <label>
                        <span className="users-caption">
                            Full name <span className="users-optional">optional</span>
                        </span>
                        <input value={draft.name} autoComplete="off"
                            onChange={e => setDraft({ ...draft, name: e.target.value })} />
                    </label>
                </div>
                {/* Groups and the invitation toggle are separate blocks on purpose. Sharing
                    one row made "Send an invitation email" read as a fourth group. */}
                <div className="users-block">
                    <span className="users-caption">Groups <span className="users-required">at least one</span></span>
                    <div className="users-checks">
                        {groups.map(group => (
                            <label key={group} className="users-check">
                                <input type="checkbox" checked={draft.groups.includes(group)}
                                    onChange={e => setDraft({
                                        ...draft,
                                        groups: e.target.checked
                                            ? [...draft.groups, group]
                                            : draft.groups.filter(g => g !== group),
                                    })} />
                                {group}
                            </label>
                        ))}
                    </div>
                </div>

                {/* Button shares this row with the invitation toggle rather than sitting
                    on its own line below the hint — one less row of form, so more of the
                    user table is visible without scrolling. */}
                <div className="users-block users-submit">
                    <div className="users-invite">
                        <label className="users-check">
                            <input type="checkbox" checked={draft.invite}
                                onChange={e => setDraft({ ...draft, invite: e.target.checked })} />
                            Send an invitation email
                        </label>
                        {/* Only shown when unticked. Ticked, it just restated the label and
                            cost a row on every load. Unticked, it warns about a consequence
                            visible nowhere else: the account is created but unusable. */}
                        {!draft.invite && (
                            <p className="users-hint">
                                No email is sent — set them a password or they cannot sign in.
                            </p>
                        )}
                    </div>

                    <button type="submit" className="users-primary" disabled={busy === 'create'}>
                        {busy === 'create' ? 'Creating…' : 'Create user'}
                    </button>
                </div>
            </form>

            <h3 className="users-section">
                {loading ? 'Loading…' : `${users.length} ${users.length === 1 ? 'user' : 'users'}`}
            </h3>

            <div className="users-table" role="table">
                <div className="users-row users-head" role="row">
                    <span role="columnheader">User</span>
                    <span role="columnheader">Status</span>
                    <span role="columnheader">Groups</span>
                    <span role="columnheader">Actions</span>
                </div>

                {users.map(user => (
                    <div className="users-row" role="row" key={user.username}>
                        <span role="cell">
                            <strong>{user.username}</strong>
                            <em className="users-email">{user.email}</em>
                        </span>
                        <span role="cell">
                            <span className={`users-pill ${user.enabled ? 'ok' : 'off'}`}>
                                {user.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                            <em className="users-status">{user.status}</em>
                        </span>
                        <span role="cell" className="users-groups">
                            {groups.map(group => (
                                <label key={group} className="users-check">
                                    <input type="checkbox"
                                        checked={user.groups.includes(group)}
                                        disabled={busy === `groups:${user.username}`}
                                        onChange={() => toggleGroup(user, group)} />
                                    {group}
                                </label>
                            ))}
                        </span>
                        <span role="cell" className="users-actions">
                            <button onClick={() => setEnabled(user, !user.enabled)}
                                disabled={busy === `enabled:${user.username}`}>
                                {user.enabled ? 'Disable' : 'Enable'}
                            </button>
                            {/* Cognito only accepts RESEND while the user has never set
                                their own password. */}
                            {user.status === 'FORCE_CHANGE_PASSWORD' && (
                                <button onClick={() => resendInvite(user)}
                                    disabled={busy === `resend:${user.username}`}>
                                    Resend invite
                                </button>
                            )}
                            <button className="users-danger" onClick={() => removeUser(user)}
                                disabled={busy === `delete:${user.username}`}>
                                Delete
                            </button>
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default UsersCRUD;
